import type { RewardBundle } from '@vm/shared';

/** 7-day login cycle. Missing a day restarts the streak at day 1. */
export const DAILY_LOGIN_REWARDS: readonly RewardBundle[] = [
  { coins: 100 },
  { coins: 150 },
  { gems: 5 },
  { coins: 250 },
  { coins: 300 },
  { gems: 10 },
  { coins: 500, gems: 25 },
];

/**
 * Streak bonus: gems of the cycle's 1st, 2nd, 3rd... time through in a row are multiplied by
 * these (the last one repeats). Players who log in every day are the game's best ambassadors:
 * over a 13-week season the streak pays 1,440 gems instead of 520, close to two passes with the
 * weekly quests. Coins are not multiplied (they buy weapons).
 */
export const LOGIN_STREAK_GEM_MULTIPLIERS: readonly number[] = [1, 2, 3];

/** Gem multiplier on streak day `day` (1-based, counts up as long as no day is missed). */
export function loginStreakMultiplier(day: number, cycleLength: number = DAILY_LOGIN_REWARDS.length): number {
  const week = Math.floor((Math.max(1, Math.floor(day)) - 1) / Math.max(1, cycleLength));
  return LOGIN_STREAK_GEM_MULTIPLIERS[Math.min(week, LOGIN_STREAK_GEM_MULTIPLIERS.length - 1)];
}

/**
 * Reward for streak day `day` (1-based, unbounded): the cycle's day (wrapping) with the streak's
 * gem multiplier. `rewards` is the live cycle (defaults to the built-in one).
 */
export function dailyLoginReward(day: number, rewards: readonly RewardBundle[] = DAILY_LOGIN_REWARDS): RewardBundle {
  const n = rewards.length || DAILY_LOGIN_REWARDS.length;
  const list = rewards.length ? rewards : DAILY_LOGIN_REWARDS;
  const i = (((Math.floor(day) - 1) % n) + n) % n;
  const out = { ...list[i] };
  if (out.gems) out.gems *= loginStreakMultiplier(day, n);
  return out;
}

/** UTC day key ('YYYY-MM-DD') shifted by `days`. */
export function shiftDayKey(dayKey: string, days: number): string {
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export interface DailyLoginState {
  /** Streak day claimed today, or the day that the next claim will grant (counts past the cycle). */
  day: number;
  claimedToday: boolean;
  /** Reward of the next claim (tomorrow's when already claimed today). */
  nextReward: RewardBundle;
}

/**
 * Login state from the last claim (`lastDay` UTC key + the streak day claimed then) and today's
 * key. Consecutive days advance the streak (the cycle wraps, the streak keeps counting for its
 * bonus); a gap restarts at day 1.
 */
export function dailyLoginState(lastDay: string | null, lastCycleDay: number, today: string, rewards: readonly RewardBundle[] = DAILY_LOGIN_REWARDS): DailyLoginState {
  if (lastDay === today) return { day: lastCycleDay, claimedToday: true, nextReward: dailyLoginReward(lastCycleDay + 1, rewards) };
  const day = lastDay && shiftDayKey(lastDay, 1) === today ? lastCycleDay + 1 : 1;
  return { day, claimedToday: false, nextReward: dailyLoginReward(day, rewards) };
}
